/**
 * spinner-wave.js
 * Vague scrollante utilisée comme indicateur de chargement (PageSpinner),
 * pendant les transitions de page gérées par le routeur JS — en lieu et
 * place de l'ancien pentagramme animé.
 *
 * Contrairement à header-wave.js (décoratif, suit la souris, joue un son
 * au clic) : ici la vague est purement autonome, juste un défilement
 * continu, démarré/arrêté à la demande par PageSpinner plutôt que
 * rattaché au cycle de vie du DOM via des attributs data-*.
 */

const SAMPLES = 48

export function createSpinnerWave(svg, userOptions) {
  const path = svg.querySelector('.spinner-wave__path')

  const options = Object.assign(
    {
      // Convention alignée sur header-wave.js (viewBox 1200 de large) :
      // la vague s'étend maintenant sur toute la largeur de la page.
      viewBoxWidth: 1200,
      viewBoxHeight: 80,
      wavelengths: 6,
      amplitude: 18,
      // Défilement volontairement plus rapide qu'un header-wave décoratif :
      // doit se lire immédiatement comme "chargement en cours".
      phaseSpeed: 3,
    },
    userOptions || {}
  )

  const {viewBoxWidth: W, viewBoxHeight: H, wavelengths, amplitude, phaseSpeed} = options
  const MID = H / 2
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`)

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  let phase = 0
  let lastFrameTime = null
  let rafId = null

  function pointsAt(currentPhase) {
    const pts = []
    for (let i = 0; i <= SAMPLES; i++) {
      const x = (i / SAMPLES) * W
      const angle = (x / W) * Math.PI * 2 * wavelengths + currentPhase
      const y = MID + Math.sin(angle) * amplitude
      pts.push(`${x.toFixed(1)},${y.toFixed(1)}`)
    }
    return pts
  }

  function draw(now) {
    if (lastFrameTime === null) lastFrameTime = now
    const dt = Math.min((now - lastFrameTime) / 1000, 0.1)
    lastFrameTime = now
    phase += phaseSpeed * dt

    path.setAttribute('d', `M${pointsAt(phase).join(' L')}`)
    rafId = requestAnimationFrame(draw)
  }

  return {
    start() {
      if (rafId !== null) return
      if (prefersReducedMotion) {
        // Rendu figé pour les personnes qui préfèrent moins de mouvement.
        path.setAttribute('d', `M${pointsAt(0).join(' L')}`)
        return
      }
      lastFrameTime = null
      rafId = requestAnimationFrame(draw)
    },
    stop() {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = null
    },
  }
}
